import type { CognitiveDistortion, ThoughtRecordDraft } from '@adhd/shared';
import { Fragment, useState, type ReactNode } from 'react';
import { RatingSlider } from './RatingSlider.js';

/**
 * The parts of a worksheet that are lists rather than one box of text: the
 * feelings, and the thinking traps.
 *
 * One copy, used by the worksheet you fill in and the worksheet you change
 * afterwards. A drafted worksheet used to be shown as read-only paragraphs and
 * saved as it was — the evidence and the app's trap picks could not be changed
 * before saving or on the record after it, under a sentence inviting him to
 * change anything it got wrong (13 Sep). Every field a worksheet saves is now
 * edited with the same controls, wherever it is edited.
 */

export const EMOTION_SUGGESTIONS = [
  'frustrated', 'dismissed', 'invalidated', 'anxious', 'ashamed',
  'hurt', 'angry', 'lonely', 'overwhelmed', 'embarrassed', 'rejected', 'defeated',
];

export interface FeelingBefore {
  emotion: string;
  /** Null when he named it without saying how strong: "Not said yet", and not saved until set (hasStrength). */
  intensityBefore: number | null;
}

/** One item per line, blank lines dropped — the evidence columns' shape. */
export function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * A draft, as the values of a worksheet form.
 *
 * Shared by the two ways a draft arrives — a conversation on the worksheet
 * screen, and a journal entry — so they cannot fill the form differently.
 * An unscored feeling stays unscored and reads "Not said yet". It used to start
 * at 60 — what tapping a chip by hand gives — and was saved as 60 if he walked
 * past it, a number he never gave (13 Sep).
 */
export function formFromDraft(draft: ThoughtRecordDraft): {
  situation: string;
  thought: string;
  beliefBefore: number | null;
  emotions: FeelingBefore[];
  evidenceFor: string;
  evidenceAgainst: string;
  response: string;
  traps: string[];
} {
  return {
    situation: draft.situation,
    thought: draft.automaticThought,
    beliefBefore: draft.beliefBefore,
    // One card per word: the cards are keyed and removed by the word, so a
    // feeling named twice kept one slider for two and Remove took both. The
    // first mention that has a strength wins, else the first mention.
    emotions: [...new Set(draft.emotions.map((e) => e.emotion))].map((word) => {
      const said = draft.emotions.filter((e) => e.emotion === word);
      const scored = said.find((e) => e.intensityBefore !== null) ?? said[0]!;
      return { emotion: word, intensityBefore: scored.intensityBefore };
    }),
    evidenceFor: draft.evidence.filter((e) => e.side === 'for').map((e) => e.text).join('\n'),
    evidenceAgainst: draft.evidence.filter((e) => e.side === 'against').map((e) => e.text).join('\n'),
    response: draft.response ?? '',
    traps: [...new Set(draft.suggestedDistortions)],
  };
}

/**
 * The sentence of his entry a word came from, for "Because you said".
 *
 * Plain matching, no model: the first sentence holding the word whole and in any
 * case, cut to a window around it when long. No match gives nothing, and then
 * nothing is shown — a line saying the app could not find it would only make
 * him doubt the rest.
 */
export function saidSentence(text: string, word: string): string | null {
  const escaped = word.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  if (!escaped) return null;
  const hit = new RegExp(`(^|[^\\p{L}\\p{N}'’])(${escaped})(?=$|[^\\p{L}\\p{N}'’])`, 'iu');
  // Sentences without lookbehind, which older Safari does not have.
  for (const raw of text.match(/[^.!?…\n]+[.!?…]*/g) ?? []) {
    const sentence = raw.trim();
    const m = hit.exec(sentence);
    if (!m) continue;
    if (sentence.length <= 140) return sentence;
    const at = m.index + m[1]!.length;
    let from = Math.max(0, at - 60);
    let to = Math.min(sentence.length, at + m[2]!.length + 60);
    if (from > 0) from = sentence.indexOf(' ', from) + 1 || from;
    if (to < sentence.length) to = sentence.lastIndexOf(' ', to) > at ? sentence.lastIndexOf(' ', to) : to;
    return `${from > 0 ? '…' : ''}${sentence.slice(from, to).trim()}${to < sentence.length ? '…' : ''}`;
  }
  return null;
}

/** The key to dashed and solid, wherever something dashed is on screen. */
export function PickLegend({ appLabel, note }: { appLabel: string; note: string }): JSX.Element {
  return (
    <div className="pick-legend">
      <span>
        <span className="pick-key pick-key-app" aria-hidden="true" />
        {appLabel}
      </span>
      <span>
        <span className="pick-key" aria-hidden="true" />
        yours
      </span>
      <span className="faint">{note}</span>
    </div>
  );
}

/** Whether a quoted phrase is really in his entry (case and spacing aside). */
function inEntry(phrase: string, entryText: string | null): boolean {
  if (!entryText) return false;
  const norm = (t: string): string => t.toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();
  return norm(entryText).includes(norm(phrase));
}

/**
 * A question, with his quoted words in the reading face and his feeling words as
 * the app's tags. A quote is set as his only when those words are in his entry:
 * the drafter may tidy wording, and the reading face says "you said this".
 */
function askParts(question: string, feelingWords: readonly string[], entryText: string | null): ReactNode[] {
  const words = [...feelingWords].filter((w) => w.trim()).sort((a, b) => b.length - a.length);
  const tagWords = (text: string, key: string): ReactNode[] => {
    if (words.length === 0) return [text];
    const pattern = new RegExp(
      `(^|[^\\p{L}\\p{N}])(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})(?=$|[^\\p{L}\\p{N}])`,
      'giu',
    );
    const out: ReactNode[] = [];
    let last = 0;
    for (const m of text.matchAll(pattern)) {
      const start = m.index! + m[1]!.length;
      if (start > last) out.push(text.slice(last, start));
      out.push(
        <span className="said-tag" key={`${key}-${start}`}>
          {m[2]}
        </span>,
      );
      last = start + m[2]!.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of question.matchAll(/"([^"]+)"|“([^”]+)”/g)) {
    if (m.index! > last) out.push(...tagWords(question.slice(last, m.index), `t${last}`));
    const quoted = m[1] ?? m[2] ?? '';
    out.push(
      inEntry(quoted, entryText) ? (
        <q className="said" key={`q${m.index}`}>
          {quoted}
        </q>
      ) : (
        <Fragment key={`q${m.index}`}>“{quoted}”</Fragment>
      ),
    );
    last = m.index! + m[0].length;
  }
  if (last < question.length) out.push(...tagWords(question.slice(last), `t${last}`));
  return out;
}

/**
 * What the draft could not find in his entry, on the step it is about.
 *
 * These were a bullet list on the first screen, in plain prose, about things
 * several screens away (13 Sep). A question here is the app's, so its box is
 * dashed; nothing in it is tappable — he answers in the field below.
 */
export function StepAsk({
  questions,
  feelingWords = [],
  entryText = null,
}: {
  questions: readonly string[];
  feelingWords?: readonly string[];
  /** His entry, so a quote is shown as his only when it is his. */
  entryText?: string | null;
}): JSX.Element | null {
  if (questions.length === 0) return null;
  return (
    <div className="step-ask">
      <span className="step-ask-eyebrow">Your entry didn&rsquo;t say</span>
      {questions.map((q, i) => (
        <p key={i}>
          {askParts(q, feelingWords, entryText).map((part, j) => (
            <Fragment key={j}>{part}</Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

export function FeelingsEditor({
  emotions,
  onChange,
  offered = [],
  appPicked = [],
  startAt = 60,
  because,
}: {
  emotions: FeelingBefore[];
  /** The next feelings, and which of them are still the app's (unchanged when there are none). */
  onChange: (next: FeelingBefore[], appPicked: string[]) => void;
  /** Words the draft took from his entry. They lead the chip row and stay in it once taken off. */
  offered?: readonly string[];
  /** Which chosen words are still the app's: dashed until he rates one or takes it off. */
  appPicked?: readonly string[];
  /** Where a newly tapped feeling starts. A drafted worksheet passes null, so it reads Not said yet. */
  startAt?: number | null;
  /** The sentence of his entry a drafted word came from, when it can be found. */
  because?: (word: string) => string | null;
}): JSX.Element {
  // Words he added by hand stay in the row too, so one taken off can be put back.
  const [own, setOwn] = useState<string[]>([]);
  const [typed, setTyped] = useState('');
  const chips = [...new Set([...offered, ...EMOTION_SUGGESTIONS, ...own, ...emotions.map((e) => e.emotion)])];
  const addOwn = (): void => {
    const word = typed.trim().toLowerCase().slice(0, 40);
    setTyped('');
    if (!word || emotions.some((e) => e.emotion === word) || emotions.length >= 12) return;
    setOwn((prev) => (prev.includes(word) ? prev : [...prev, word]));
    onChange([...emotions, { emotion: word, intensityBefore: startAt }], [...appPicked]);
  };
  const anyApp = emotions.some((e) => appPicked.includes(e.emotion));
  return (
    <>
      {anyApp && <PickLegend appLabel="the app’s pick" note="Tap one to take it off." />}
      <div className="chips">
        {chips.map((name) => {
          const on = emotions.some((e) => e.emotion === name);
          const app = on && appPicked.includes(name);
          return (
            <button
              key={name}
              type="button"
              className={app ? 'chip chip-app' : 'chip'}
              aria-pressed={on}
              aria-label={app ? `${name} (the app’s pick)` : undefined}
              onClick={() =>
                on
                  ? onChange(
                      emotions.filter((e) => e.emotion !== name),
                      appPicked.filter((w) => w !== name),
                    )
                  : onChange([...emotions, { emotion: name, intensityBefore: startAt }], [...appPicked])
              }
            >
              {name}
            </button>
          );
        })}
      </div>

      {/* A word of his own. The suggestions are twelve; what he felt may not be
          one of them, and a drafted word he saved without a strength had no way
          back onto the worksheet (13 Sep). */}
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <label htmlFor="own-feeling" className="sr-only">
          Another feeling, in your words
        </label>
        <input
          id="own-feeling"
          className="input"
          style={{ flex: 1, minWidth: 0 }}
          value={typed}
          maxLength={40}
          placeholder="Another word for it"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOwn();
            }
          }}
        />
        <button type="button" className="btn btn-secondary" onClick={addOwn} disabled={!typed.trim() || emotions.length >= 12}>
          Add
        </button>
      </div>

      {emotions.map((e, i) => {
        const app = appPicked.includes(e.emotion);
        const said = offered.includes(e.emotion) ? (because?.(e.emotion) ?? null) : null;
        return (
          <div className={app ? 'card card-app stack-sm stack' : 'card stack-sm stack'} key={e.emotion}>
            {/* Removable from here, not only by un-tapping a chip.
                Anything that did not come from the suggestion list — a word
                drafted from what he said — has no chip to un-tap, so until this
                existed those were permanent. Landing on a record that says you
                felt "off" with no way to take it off is worse than it not being
                filled in at all. */}
            <div className="row-between">
              <label htmlFor={`int-${e.emotion}`}>
                <strong>{e.emotion}</strong>
              </label>
              <button
                type="button"
                className="btn btn-ghost btn-compact"
                aria-label={`Remove ${e.emotion}`}
                onClick={() =>
                  onChange(
                    emotions.filter((x) => x.emotion !== e.emotion),
                    appPicked.filter((w) => w !== e.emotion),
                  )
                }
              >
                Remove
              </button>
            </div>
            {said && (
              <p className="because">
                Because you said: <q className="said">{said}</q>
              </p>
            )}
            <RatingSlider
              id={`int-${e.emotion}`}
              value={e.intensityBefore}
              low="Barely"
              high="As strong as it gets"
              onChange={(v) => {
                const next = [...emotions];
                next[i] = { ...e, intensityBefore: v };
                // Rated, it is his.
                onChange(next, appPicked.filter((w) => w !== e.emotion));
              }}
            />
          </div>
        );
      })}
    </>
  );
}

/**
 * "Any of these fit?" — the fourteen traps, with the app's picks marked.
 *
 * The owner chose (10 Sep) to keep the traps the app picks when it drafts a
 * worksheet, marked as its picks. Marked is not the same as fixed: an app pick
 * is a dashed chip, pressed, and one tap takes it off. A trap he taps is his —
 * including one the app had picked and he took off and put back — so the dash
 * only ever means the app chose it and he left it there.
 */
export function TrapPicker({
  distortions,
  picked,
  appPicked,
  onChange,
}: {
  distortions: CognitiveDistortion[];
  picked: string[];
  appPicked: string[];
  onChange: (next: { picked: string[]; appPicked: string[] }) => void;
}): JSX.Element {
  // Only over chips on screen: a key the app made up has no chip to be dashed.
  const anyApp = distortions.some((d) => picked.includes(d.key) && appPicked.includes(d.key));
  return (
    <section className="stack">
      <h2>Any of these fit?</h2>
      <p className="muted">Optional. Noticing the pattern makes it easier to catch next time.</p>
      {anyApp && <PickLegend appLabel="the app’s pick" note="Its suggestion, not your answer. Tap one to take it off." />}
      <div className="chips">
        {distortions.map((d) => {
          const on = picked.includes(d.key);
          const app = on && appPicked.includes(d.key);
          return (
            <button
              key={d.key}
              type="button"
              className={app ? 'chip chip-app' : 'chip'}
              aria-pressed={on}
              aria-label={app ? `${d.name} (the app's pick)` : undefined}
              onClick={() =>
                onChange(
                  on
                    ? { picked: picked.filter((k) => k !== d.key), appPicked: appPicked.filter((k) => k !== d.key) }
                    : { picked: [...picked, d.key], appPicked },
                )
              }
              title={d.description}
            >
              {d.name}
            </button>
          );
        })}
      </div>
      {picked.length > 0 && (
        <div className="banner stack-sm stack">
          {distortions
            .filter((d) => picked.includes(d.key))
            .map((d) => (
              <div key={d.key}>
                <strong>{d.name}</strong>
                <br />
                {d.reframePrompt}
              </div>
            ))}
        </div>
      )}
    </section>
  );
}
